import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, RadioIcon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { profileCoordinates, publicReportStatusLabels, publicReports, reportCategories, type PublicReport, type PublicReportCategory } from '../../data/demandes';
type Filter = 'all' | PublicReportCategory;
const filters: Filter[] = ['all', 'danger', 'voirie', 'eclairage', 'arbre'];
const statusStyles: Record<PublicReport['status'], string> = {
  nouveau: 'bg-ville-lime text-ville-navy',
  'in-progress': 'bg-ville-indigo-soft text-ville-indigo',
  resolue: 'bg-slate-100 text-slate-500'
};
function pinIcon(report: PublicReport, active: boolean): L.DivIcon {
  const color = reportCategories[report.category].color;
  const size = active ? 34 : 24;
  return L.divIcon({
    className: 'atna-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30 ${active ? 'ring-2 ring-ville-navy/20' : ''}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function PublicReportsMap() {
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string>(publicReports[0].id);
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const visible = useMemo(() => filter === 'all' ? publicReports : publicReports.filter(report => report.category === filter), [filter]);
  const selected = visible.find(report => report.id === selectedId) ?? visible[0];
  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newCount = publicReports.filter(report => report.status === 'nouveau').length;
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: profileCoordinates,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    // OpenStreetMap tiles: free and no API key needed.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: '#04302f',
      fillOpacity: 1
    }).bindTooltip('Your address').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    visible.forEach(report => {
      L.marker([report.lat, report.lng], {
        icon: pinIcon(report, report.id === selected?.id),
        keyboard: true,
        title: `${reportCategories[report.category].label} — ${report.title}`,
        alt: `${reportCategories[report.category].label} — ${report.title}`,
        zIndexOffset: report.id === selected?.id ? 500 : 0
      }).on('click', () => setSelectedId(report.id)).on('keypress', () => setSelectedId(report.id)).addTo(layer);
    });
  }, [visible, selected?.id]);
  useEffect(() => {
    if (!mapRef.current || !selected) return;
    mapRef.current.panTo([selected.lat, selected.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selected?.id]);
  const step = (direction: -1 | 1) => {
    if (visible.length === 0) return;
    const next = (selectedIndex + direction + visible.length) % visible.length;
    setSelectedId(visible[next].id);
  };
  const toggleConfirm = (id: string) => {
    setConfirmedIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };
  return <section aria-labelledby="carte-publique-titre"
  /* `isolate` keeps the Leaflet layers (controls, floating card) in their own stacking
     context, otherwise they sit on top of the ATNA dialog. */ className="relative isolate z-0 overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-844" data-mp="2kau8bwt9kgi6">
      <div className="px-5 pb-4 pt-5 sm:px-6" data-id="element-845" data-mp="26kh0rvkab3td">
        <div className="flex items-start justify-between gap-4" data-id="element-846" data-mp="21k2pm4ndzdr4">
          <div data-id="element-847" data-mp="21lvzdstaqe3c">
            <h2 id="carte-publique-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-848" data-mp="1wkq5vsrtamsr">
              Reports around you
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-849" data-mp="1skpg5bz9ip6e">
              {publicReports.length} active reports · {newCount} new today
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-850" data-mp="3mwmdflkdyp9d">
            <RadioIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-851" data-mp="18ov5up8ouygb" />
            Live
          </span>
        </div>

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-852" data-mp="1dmd7dukdmbea">
          {filters.map(option => {
          const active = filter === option;
          const label = option === 'all' ? 'All' : reportCategories[option].label;
          return <li key={option} data-id="element-853" data-mp="pqimybrz6a7j">
                <button type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-854" data-mp="prae9aejhls0">
                  {option !== 'all' && <span className="h-2 w-2 rounded-full" style={{
                backgroundColor: reportCategories[option].color
              }} aria-hidden="true" data-id="element-855" data-mp="2f4xhxdtl6tbh" />}
                  {label}
                </button>
              </li>;
        })}
        </ul>
      </div>

      <div className="relative" data-id="element-856" data-mp="b3ykf4rwfsv6">
        <div ref={containerRef} role="application" aria-label="Map of public reports in Bonnyville" className="h-[300px] w-full bg-ville-mist sm:h-[380px]" data-id="element-857" data-mp="p1wwu4mntf81" />

        {selected ? <motion.div key={selected.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-ville-navy/10 sm:inset-x-4" data-id="element-858" data-mp="k0h8o3p6qslf">
            <div className="flex items-start gap-3" data-id="element-859" data-mp="19pxcwln0s4e">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{
            backgroundColor: reportCategories[selected.category].color
          }} aria-hidden="true" data-id="element-860" data-mp="39qkndbhc6i12" />
              <div className="min-w-0 flex-1" data-id="element-861" data-mp="1k9ytl5xq2dg3">
                <div className="flex flex-wrap items-center gap-2" data-id="element-862" data-mp="1k95y5147rad9">
                  <h3 className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-863" data-mp="1p28rb852whsx">{selected.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyles[selected.status]}`} data-id="element-864" data-mp="1bb3ltpf14vao">
                    {publicReportStatusLabels[selected.status]}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-865" data-mp="22yvtbpbw1y5p">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-866" data-mp="27xyw134ebsai" />
                  <span className="truncate" data-id="element-867" data-mp="1tzs3m2i7uki8">{selected.address}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-400" data-id="element-868" data-mp="1yy1zkfbrlp4w">
                  {selected.distance} away · {selected.reportedAgo}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" data-id="element-869" data-mp="nkhsl49opfkq">
                <button type="button" onClick={() => step(-1)} aria-label="Previous report" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-870" data-mp="nhvc2f4tfpqd">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-871" data-mp="10as6cly0u46m" />
                </button>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400" data-id="element-872" data-mp="1xysg16pai8ov">
                  {selectedIndex + 1}/{visible.length}
                </span>
                <button type="button" onClick={() => step(1)} aria-label="Next report" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-873" data-mp="1t1adpz5s2rpu">
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-874" data-mp="2by1y6bx23cvh" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3" data-id="element-875" data-mp="2ja9iaglyle7c">
              <p className="flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-876" data-mp="2o92lng7j7ots">
                <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-877" data-mp="2t74egsurf2ig" />
                {selected.confirmations + (confirmedIds.includes(selected.id) ? 1 : 0)} people confirm this
              </p>
              <button type="button" onClick={() => toggleConfirm(selected.id)} aria-pressed={confirmedIds.includes(selected.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${confirmedIds.includes(selected.id) ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-ville-navy text-white hover:bg-ville-navy/90'}`} data-id="element-878" data-mp="32711kq1i3tbe">
                <ThumbsUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-879" data-mp="3pwv86pv88ln7" />
                {confirmedIds.includes(selected.id) ? 'Confirmed' : 'I can confirm this'}
              </button>
            </div>
          </motion.div> : <div className="absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-xl shadow-ville-navy/10" data-id="element-880" data-mp="30p2u8y1ys6ve">
            No reports in this category right now.
          </div>}
      </div>
    </section>;
}